import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptions } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import InsurancePane from 'components/patient-entry/upsert/InsurancePane';
import PatientPane from 'components/patient-entry/upsert/PatientPane';
import PatientCardData from 'components/patient/upsert/PatientCardData';
import {
  FieldProps,
  FieldType,
  getFieldType,
  requiredImpl,
  VALIDATED_FIELDS
} from 'components/patient/utils/patientUtils';
import { FormApi, FormState } from 'final-form';
import createDecorator from 'final-form-calculate';
import moment from 'moment';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form'
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Modal, RadioProps, SemanticWIDTHS } from 'semantic-ui-react';
import { findDoctorById, searchDoctorsForInput } from 'services/doctorServices';
import { addPatient, editPatient } from 'services/patientServices';
import styled from 'styled-components';
import { CivilStatus } from 'ts-types/api.enums';
import {
  CountryDto,
  DoctorDto,
  LanguageDto,
  PatientDto,
  PatientGuarantorDto,
  UpsertPatientDto,
  ZipDataDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { isValueEmpty } from 'utils/validatorUtils';

const PatientFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow: hidden;
`;

const PatientFormModalContentContainer = styled(FormModalContentContainer)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PatientForm = styled.form`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PatientFormContent = styled(FormContent)`
  overflow-y: auto;
  margin: 2px;

  .ui.radio.checkbox {
    padding-bottom: 6px;
    padding-top: 6px;
  }
`;

const StyledError = styled.div`
  .ui.grid {
    margin: 0;
  }
`;

const StyledSpan = styled.span`
  color: #b58105;
`;

const StyledCardDataModal = styled(Modal)`
  && {
    max-width: 700px;
  }
`;

interface State extends IndexSignature {
  componentDidMount: boolean,
  countries: Array<DropdownOption>,
  languages: Array<DropdownOption>,
  civilStatuses: Array<DropdownOption>,
  billTexts: Array<DropdownOption>,
  mandates: Array<DropdownOption>,
  representingDoctor: Array<DropdownOption>,
  patientGuarantors: Array<PatientGuarantorDto>,
  genders: Array<RadioProps>,
  bGenders: Array<RadioProps>,
  errorMessage?: string,
  initialCities: Array<DropdownOption>,
  bCities: Array<DropdownOption>,
  initialZip: Array<DropdownOption>,
  bZip: Array<DropdownOption>,
  patientCardDataModalOpen: boolean,
  cancelTokenSource: CancelTokenSource
}

export interface IndexedZipData extends IndexSignature, ZipDataDto {
}

export enum PatientEditMode {
  BASIC_DATA = "BASIC_DATA",
  INSURANCE = "INSURANCE"
}

interface Props extends AuthConsumerRenderProps, MandateConsumerRenderProps, MessageSourceProps {
  editMode?: PatientEditMode,
  patient?: UpsertPatientDto,
  patientId: number,
  onPatientSave: (patient?: Partial<PatientDto>) => void,
  onCancel: () => void,
  countries: Array<CountryDto>,
  languages: Array<LanguageDto>
}

class UpsertPatient extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {patient, countries = [], languages = [], getMessage, currentMandates} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    const civilStatusLabels: Array<string> = Object.values(CivilStatus);
    const billTextLabels: Array<string> = ["german", "french", "italian", "english"];
    const billTextKeys: Array<string> = ["de", "fr", "it", "en"];
    const genderLabels: Array<string> = ["male", "female", "other"];
    const bGenderLabels: Array<string> = ["bmale", "bfemale", "bother", "bcompany"];
    const bGenderValues: Array<number> = [0, 1, 2, 3];

    const initialCities: Array<DropdownOption> = [];
    const bCities: Array<DropdownOption> = [];
    const initialZip: Array<DropdownOption> = [];
    const bZip: Array<DropdownOption> = [];

    if (patient) {

      if (patient.city) {
        initialCities.push({
          key: patient.city,
          text: patient.city,
          value: patient.city,
          predefinedOption: true
        })
      }

      if (patient.zip) {
        initialZip.push({
          key: patient.zip,
          text: patient.zip,
          value: patient.zip,
          predefinedOption: true
        })
      }

      if (patient.bcity) {
        bCities.push({
          key: patient.bcity,
          text: patient.bcity,
          value: patient.bcity,
          predefinedOption: true
        })
      }

      if (patient.bzip) {
        bZip.push({
          key: patient.bzip,
          text: patient.bzip,
          value: patient.bzip,
          predefinedOption: true
        })
      }
    }

    this.state = {
      componentDidMount: false,
      countries: mapToDropdownOptionArray(countries, "name", "shortname"),
      languages: mapToDropdownOptionArray(languages, "description"),
      civilStatuses: stringArrayToDropdownOptionArray(civilStatusLabels, getMessage, "enum.civilStatus"),
      billTexts: stringArrayToDropdownOptionArray(billTextLabels, getMessage, "billText", billTextKeys),
      mandates: mapToDropdownOptionArray(currentMandates!, "name"),
      representingDoctor: [],
      patientGuarantors: patient && patient.patientGuarantors ? patient.patientGuarantors : [],
      patientCodes: patient && patient.patientCodes ? patient.patientCodes : [],
      diagnoses: [],
      patientDiagnoses: patient && patient.patientDiagnoses ? patient.patientDiagnoses : [],
      genders: generateRadioButtonOptions(genderLabels, "gender", getMessage),
      bGenders: generateRadioButtonOptions(bGenderLabels, "bgender", getMessage, "gender", bGenderValues),
      initialCities: initialCities,
      initialZip: initialZip,
      bCities: bCities,
      bZip: bZip,
      problematicPatient: false,
      patientCardDataModalOpen: false,
      cancelTokenSource
    };

    if (patient && patient.representingDoctorId) {
      this.initialRepresentingDoctorData();
    }
  }

  componentDidMount() {
    this.setState({componentDidMount: true})
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  initialPatientValues = (): Partial<UpsertPatientDto> => {
    const {countries, languages, currentUser} = this.props;

    let countryId = undefined;
    if (countries.length > 0) {
      const country = countries.find(country => country.isDefault);
      countryId = country ? country.id : countries[0].id;
    }

    return {
      active: true,
      sex: 0,
      mandateId: currentUser!.userConfig!.mandateId,
      reminder: true,
      bsex: 0,
      countryId: countryId,
      bcountryId: countryId,
      language: "de",
      languageId: languages && languages.length > 0 ? languages[0].id : undefined
    };
  };

  openPatientCardDataModal = () => {
    this.setState({
      patientCardDataModalOpen: true
    })
  };

  closePatientCardDataModal = () => {
    this.setState({
      patientCardDataModalOpen: false
    })
  };

  updatePatientDataFromCard = (patientGuarantors: Array<PatientGuarantorDto>, cardData: any, form: FormApi) => {
    const {countries} = this.props;

    form.batch(() => {
      if (cardData.name)
        form.change('name', cardData.name);
      if (cardData.firstName)
        form.change('firstName', cardData.firstName);
      if (cardData.birthDate)
        form.change('birthDate', cardData.birthDate);
      if (cardData.ahvNumber)
        form.change('ahvNumber', cardData.ahvNumber);
      if (cardData.country)
        form.change('countryId', countries.find(c => c.shortName === cardData.country)!.id);
      if (cardData.sex)
        form.change('sex', cardData.sex);
    });

    this.setState({
      patientGuarantors: patientGuarantors
    })
  }


  initialRepresentingDoctorData = (): void => {
    const {patient} = this.props;
    const {cancelTokenSource} = this.state;
    if (patient && patient.representingDoctorId) {
      findDoctorById(patient.representingDoctorId, cancelTokenSource)
          .then(response => {
            const representingDoctor: DropdownOption = {
              key: patient.representingDoctorId,
              text: `${response.name}, ${response.firstName ? response.firstName : ""}, ${response.city}, ${response.street ? response.street : ""}`,
              value: patient.representingDoctorId
            };

            this.setState({
              representingDoctor: [representingDoctor]
            });
          })
          .catch(noop)
    }
  };

  searchDoctors = (search: string): Promise<Array<DoctorDto>> => {

    const {cancelTokenSource} = this.state;

    return searchDoctorsForInput(search, cancelTokenSource, 75);
  };

  mapDoctors = (doctors: Array<DoctorDto>): Array<DropdownOption> => {
    return doctors.map(doctor => ({
      key: doctor.id,
      text: `${doctor.name}, ${doctor.firstName ? doctor.firstName : ""}, ${doctor.city}, ${doctor.street ? doctor.street : ""}`,
      value: doctor.id
    }));
  };

  updateZipDataFieldDecorator = (field: string) =>
      (zipData?: string, allValues?: Partial<UpsertPatientDto & IndexSignature>) => {

        if (zipData) {
          const splitZipData = zipData.split(", ");

          if (splitZipData.length == 3 && allValues && allValues[field] != zipData) {
            return zipData;
          }
        }

        return allValues ? allValues[field] : undefined;
      };

  updateCountryDecorator = (field: string) =>
      (zipData?: string, allValues?: Partial<UpsertPatientDto & IndexSignature>) => {

        if (zipData) {
          const splitZipData = zipData.split(", ");

          if (splitZipData.length == 3 && !isValueEmpty(splitZipData[2])) {
            return parseInt(splitZipData[2]);
          }
        }

        return allValues ? allValues[field] : undefined;
      };

  zipDataDecorator = createDecorator(
      {
        field: "city",
        updates: {
          zip: this.updateZipDataFieldDecorator("zip")
        }
      },
      {
        field: "zip",
        updates: {
          city: this.updateZipDataFieldDecorator("city")
        }
      },
      {
        field: "bcity",
        updates: {
          bzip: this.updateZipDataFieldDecorator("bzip")
        }
      },
      {
        field: "bzip",
        updates: {
          bcity: this.updateZipDataFieldDecorator("bcity")
        }
      }
  );

  updateValue = (field: string, allValues?: Partial<UpsertPatientDto & IndexSignature>): number | string | undefined => {

    if (!this.state.componentDidMount || (allValues && !allValues.separateBill)) {
      return allValues ? allValues[`b${field}`] : null;
    }

    if (allValues && allValues.separateBill) {

      const {getMessage} = this.props;
      const {bCities, bZip} = this.state;

      const fieldValue = allValues[field];
      if (fieldValue) {

        switch (field) {
          case "zip": {
            const foundZip = bZip.find(zip => zip.key == fieldValue);
            if (!foundZip) {
              const zipValueArray = fieldValue!.split(",");
              const newZip: DropdownOption = {
                key: fieldValue!,
                text: zipValueArray[0],
                value: fieldValue
              }

              this.setState({
                bZip: [newZip, ...bZip]
              })
            }

            break;
          }

          case "city": {
            const foundCity = bCities.find(city => city.key == fieldValue);
            if (!foundCity) {
              const cityValueArray = fieldValue!.split(",");
              const newCity: DropdownOption = {
                key: fieldValue!,
                text: cityValueArray.length > 1 ? cityValueArray[1] : cityValueArray[0],
                value: fieldValue
              }

              this.setState({
                bCities: [newCity, ...bCities]
              });
            }
            break;
          }

          case "title": {
            if (!allValues.btitle && allValues.btitle !== null) {
              if (allValues.sex == 0) {
                return getMessage("mr");
              } else if (allValues.sex == 1) {
                return getMessage("mrs");
              }
            } else {
              return allValues.btitle;
            }
          }
        }
      }

      return fieldValue;
    } else if (allValues && !allValues.separateBill && field == "sex") {
      return allValues.bsex;
    }
  };

  separateBillDecorator = createDecorator(
      {
        field: 'separateBill',
        updates: {
          bname: (value, allValues) => this.updateValue("name", allValues),
          bfirstName: (value, allValues) => this.updateValue("firstName", allValues),
          bstreet: (value, allValues) => this.updateValue("street", allValues),
          bzip: (value, allValues) => this.updateValue("zip", allValues),
          bcity: (value, allValues) => this.updateValue("city", allValues),
          bcountryId: (value, allValues) => this.updateValue("countryId", allValues),
          bsex: (value, allValues) => this.updateValue("sex", allValues),
          btitle: (value, allValues) => this.updateValue("title", allValues)
        }
      }
  );

  getFieldOptions = (field: string, pane: string): Partial<FieldProps> => {

    const {
      bGenders,
      genders,
      countries,
      civilStatuses,
      languages,
      billTexts,
      mandates,
      representingDoctor
    } = this.state;
    const {getMessage} = this.props;

    const validator: Function =
        (value: string, allValues: Partial<UpsertPatientDto>) => requiredImpl(value, allValues, field);

    const selectFields: IndexSignature = {
      countryId: countries,
      bcountryId: countries,
      civilStatus: civilStatuses,
      languageId: languages,
      language: billTexts,
      mandateId: mandates,
      representingDoctorId: representingDoctor
    };

    const remoteSearchData: IndexSignature = {
      representingDoctorId: {
        remoteSearch: true,
        remoteSearchMethod: this.searchDoctors,
        searchResultToOptionMapper: this.mapDoctors
      }
    };

    let fieldOptions: IndexSignature = {
      name: field,
      validate: VALIDATED_FIELDS.includes(field) ? validator : undefined
    };

    switch (getFieldType(field)) {
      case FieldType.INPUT: {
        fieldOptions = {
          ...fieldOptions,
          component: Input
        };
        break;
      }
      case FieldType.CHECKBOX: {
        fieldOptions = {
          ...fieldOptions,
          component: CheckBox,
          label: getMessage(`patient.${field}`)
        };
        break;
      }
      case FieldType.DATEPICKER: {
        fieldOptions = {
          ...fieldOptions,
          component: DatePicker,
          disabledAfter: new Date(moment().format("YYYY-MM-DD"))
        };
        break;
      }
      case FieldType.RADIOBUTTON: {
        fieldOptions = {
          ...fieldOptions,
          component: RadioGroup,
          radioDefinitions: pane == "patient" ? genders : bGenders
        };
        break;
      }
      case FieldType.SELECT: {
        fieldOptions = {
          ...fieldOptions,
          component: Select,
          allowAdditions: ["city", "bcity", "zip", "bzip"].includes(field),
          options: selectFields[field],
          clearable: field == "representingDoctorId",
          upward: ["mandateId", "languageId"].includes(field)
        };

        if (remoteSearchData[field]) {
          fieldOptions = {
            ...fieldOptions,
            ...remoteSearchData[field]
          }
        }

        break;
      }
    }

    return fieldOptions;
  };

  getFieldWidth = (row: Array<string>, field: string): SemanticWIDTHS | undefined => {
    if (row.length == 1) {
      if (field == 'representingDoctorId') {
        return 13;
      }
      return field != "bsex" ? 5 : 8;
    }
    return;
  };

  mapRowsToFields = (rows: Array<Array<string>>, pane: string): Array<JSX.Element> => {
    const {getMessage} = this.props;

    return rows.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {
                const isBillField = field.charAt(0) == "b" && field != "birthDate" && field != "birthName";
                const formattedField = isBillField ? field.slice(1) : field;
                return this.getFieldComponentWithHeader(
                    getMessage(`patient.${formattedField}`) + ":",
                    this.getFieldOptions(field, pane),
                    index1,
                    this.getFieldWidth(row, field));
              })
            }
          </Grid.Row>
      );
    });
  };

  addPatientGuarantor = (patientGuarantor: PatientGuarantorDto) => {

    const {patientGuarantors} = this.state;

    const modifiedPatientGuarantors = Array.from(patientGuarantors);

    modifiedPatientGuarantors.push(patientGuarantor);

    this.setState({
      patientGuarantors: modifiedPatientGuarantors
    })
  };

  editPatientGuarantor = (patientGuarantorIndex: number, patientGuarantor: PatientGuarantorDto) => {

    const {patientGuarantors} = this.state;

    const modifiedPatientGuarantors = Array.from(patientGuarantors);

    modifiedPatientGuarantors[patientGuarantorIndex] = {
      ...patientGuarantor
    };

    this.setState({
      patientGuarantors: modifiedPatientGuarantors
    })
  };

  deletePatientGuarantor = (patientGuarantorIndex: number) => {

    const {patientGuarantors} = this.state;

    const modifiedPatientGuarantors = Array.from(patientGuarantors);

    modifiedPatientGuarantors.splice(patientGuarantorIndex, 1);

    this.setState({
      patientGuarantors: modifiedPatientGuarantors
    })
  };

  createUpsertPatient = (values: Partial<UpsertPatientDto>) => {

    const {patientGuarantors, patientCodes, patientDiagnoses} = this.state;

    let zip = "";
    let bZip = "";
    let city = "";
    let bCity = "";

    if (values.zip) {
      if (values.zip.includes(', ')) {
        zip = values.zip.split(", ")[0];
      } else {
        zip = values.zip;
      }
    }

    if (values.bzip) {
      if (values.bzip.includes(', ')) {
        bZip = values.bzip.split(", ")[0];
      } else {
        bZip = values.bzip;
      }
    }

    if (values.city) {
      if (values.city.includes(', ')) {
        city = values.city.split(", ")[1];
      } else {
        city = values.city;
      }
    }

    if (values.bcity) {
      if (values.bcity.includes(', ')) {
        bCity = values.bcity.split(", ")[1];
      } else {
        bCity = values.bcity;
      }
    }

    const upsertPatientDto = {
      ...values,
      separateBill: !!values.separateBill,
      zip: zip,
      city: city,
      bzip: bZip,
      bcity: bCity,
      patientGuarantors: patientGuarantors,
      patientCodes: patientCodes,
      patientDiagnoses: patientDiagnoses,
      email: values.email ? values.email.trim() : values.email
    };

    return upsertPatientDto;
  }

  savePatient = async (values: Partial<UpsertPatientDto>) => {

    const {patientId, onPatientSave, getMandate} = this.props;
    const {cancelTokenSource} = this.state;

    const mandateId = getMandate();
    const upsertPatientDto = this.createUpsertPatient(values);

    let savedPatient: PatientDto;
    try {
      if (patientId != -1) {
        savedPatient = await editPatient(patientId, upsertPatientDto, mandateId, cancelTokenSource);
      } else {
        upsertPatientDto.mandateId = mandateId;
        savedPatient = await addPatient(upsertPatientDto, cancelTokenSource);
      }
    } catch (e) {
      this.handleError(e.response.data);
      return;
    }

    onPatientSave(savedPatient);
  };

  handleChange = ({values}: FormState) => {

    const {problematicPatient} = this.state;
    const {doctorProblematic, invoiceProblematic} = values;

    const isPatientProblematic = doctorProblematic || invoiceProblematic;
    if (problematicPatient != isPatientProblematic) {
      this.setState({
        problematicPatient: isPatientProblematic
      })
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [errorUtils.mandateNotAllowedToModifyAnotherPatients];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  getFieldComponentWithHeader = (
      header: string,
      {name, ...fieldProps}: Partial<FieldProps>,
      index?: number,
      fieldWidth: SemanticWIDTHS = 5): JSX.Element => {

    const type: string = getFieldType(name);
    return (
        <React.Fragment key={index}>
          <Grid.Column width={3} verticalAlign="middle">
            {type != FieldType.CHECKBOX ? header : ''}
          </Grid.Column>
          <Grid.Column width={fieldWidth} verticalAlign="middle">
            <Field
                name={name}
                fluid={type != FieldType.CHECKBOX ? true : undefined}
                {...fieldProps}
            />
          </Grid.Column>
        </React.Fragment>
    )
  };

  render(): React.ReactNode {

    const {patient} = this.props;

    return (
        <PatientFormModalContentContainer>
          <FinalForm
              onSubmit={this.savePatient}
              decorators={[this.separateBillDecorator, this.zipDataDecorator]}
              subscription={{submitting: true, pristine: true}}
              initialValues={patient ? patient : this.initialPatientValues()}
              render={this.renderForm}
          />
        </PatientFormModalContentContainer>
    );
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  renderForm = ({submitting, handleSubmit, form}: FormRenderProps): JSX.Element => {

    const {editMode, onCancel, getMessage, currentUser, patient} = this.props;
    const {errorMessage} = this.state;

    const userConfig = currentUser!.userConfig;
    const mandateId = userConfig.mandateId;
    const patientMandateId = patient ? patient.mandateId : 1;
    const editAllowed = !patient
        || userConfig.misantoImportEnabled
        || mandateId === patientMandateId;

    const cancelButtonTextKey = editMode == PatientEditMode.BASIC_DATA
        ? "button.cancel"
        : "patientCentral.patientEntry.actions.back";

    // @ts-ignore
    return (
        <React.Fragment>
          <PatientForm onSubmit={handleSubmit}>
            <PatientFormContainer>
              <PatientFormContent>
                <StyledError>
                  {errorMessage
                      && (
                          <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                            {errorMessage}
                          </StyledErrorMessage>
                      )
                  }
                  {this.renderFormContent(form)}
                </StyledError>

                {!editAllowed &&
                    <StyledSpan>
                      {getMessage("patient.edit.mandateNotAllowedToEdit.warning.message")}
                    </StyledSpan>
                }
              </PatientFormContent>

              <FormButtonsRow>
                <Button type="button" compact disabled={submitting} onClick={onCancel}>
                  {getMessage(cancelButtonTextKey)}
                </Button>

                <Button type="button"
                        compact
                        primary
                        floated="right"
                        onClick={(e) => {
                          // @ts-ignore
                          handleSubmit(e);
                        }}
                        disabled={submitting}
                >
                  {getMessage("patientCentral.patientEntry.actions.next")}
                </Button>
                <Button type="button"
                        floated="right"
                        compact onClick={this.openPatientCardDataModal}
                        disabled={submitting}
                >
                  {getMessage("patient.cardData")}
                </Button>
              </FormButtonsRow>
            </PatientFormContainer>
          </PatientForm>
          {this.renderPatientCardDataModal(form)}
        </React.Fragment>
    )
  };

  renderFormContent = (form: FormApi): React.ReactNode => {
    const {editMode} = this.props;
    switch (editMode) {
      case PatientEditMode.INSURANCE:
        return this.renderInsurancePane(form);
      default:
        return this.renderPatientPane(form);
    }
  }

  renderPatientPane = (form: FormApi): React.ReactNode => {

    const {countries, genders, initialCities, initialZip, civilStatuses, languages, billTexts, mandates} = this.state;

    return (
        <PatientPane
            patient={this.props.patient}
            initialCities={initialCities}
            initialZip={initialZip}
            countries={countries}
            genders={genders}
            civilStatuses={civilStatuses}
            languages={languages}
            billTexts={billTexts}
            mandates={mandates}
            countryId={form.getState().values.countryId}
        />
    )
  };

  renderInsurancePane = (form: FormApi): React.ReactNode => {

    const {patientGuarantors, bCities, bZip, bGenders, countries} = this.state;

    return (
        <InsurancePane
            mapRowsToFields={this.mapRowsToFields}
            initialCities={bCities}
            initialZip={bZip}
            genders={bGenders}
            countries={countries}
            countryId={form.getState().values.countryId}
            patientGuarantors={patientGuarantors}
            addPatientGuarantor={this.addPatientGuarantor}
            editPatientGuarantor={this.editPatientGuarantor}
            deletePatientGuarantor={this.deletePatientGuarantor}
        />
    )
  };

  updatePatientGuarantors = (patientGuarantors: Array<PatientGuarantorDto>) => {
    this.setState({
      patientGuarantors: patientGuarantors
    })
  }

  renderPatientCardDataModal = (form: FormApi): React.ReactNode => {

    const {getMessage, countries, patientId} = this.props;
    const {patientCardDataModalOpen, genders, patientGuarantors} = this.state;

    return (
        <StyledCardDataModal
            open={patientCardDataModalOpen}
            onClose={this.closePatientCardDataModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("patient.cardData")}</Modal.Header>
          <FormModalContentContainer>
            <PatientCardData
                patientForm={form}
                closeModal={this.closePatientCardDataModal}
                patientGuarantors={patientGuarantors}
                countries={countries}
                genders={genders}
                patientId={patientId}
                updatePatientDataFromCard={this.updatePatientDataFromCard}
            />
          </FormModalContentContainer>
        </StyledCardDataModal>
    )
  };

}

export default withMessages(withAuthContext(withMandateContext(UpsertPatient)));
